import { Fragment, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Activity,
  Cable,
  CalendarDays,
  Circle,
  CreditCard,
  DollarSign,
  LogIn,
  Mail,
  MessageCircle,
  Phone,
  RotateCcw,
  Send,
  SlidersHorizontal,
  Star,
  StickyNote,
  Target,
  type LucideIcon,
} from "lucide-react";
import type { CommunicationSource, CommunicationEvent } from "../../../types/crm";
import {
  useCustomerTimeline,
  TIMELINE_PAGE_SIZE,
} from "../../../helpers/hooks/use-customer-timeline";
import { closeThread } from "../../../helpers/api/crm";
import type { ComposeContext } from "../ComposeEmailModal";

const REPLYABLE_SOURCES: CommunicationSource[] = [
  "cs_ticket",
  "email_inbound",
];

// ---------------------------------------------------------------------------
// Time formatting (ET)
// ---------------------------------------------------------------------------

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function absoluteTime(isoString: string): string {
  return new Date(isoString).toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortTime(isoString: string): string {
  return new Date(isoString).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Event glyphs — mono Lucide icons, type-aware for source=slack
// ---------------------------------------------------------------------------

function getEventIcon(source: CommunicationSource, type: string): LucideIcon {
  if (source === "slack") {
    switch (type) {
      case "login":
        return LogIn;
      case "chatbot_question":
        return MessageCircle;
      case "price_update":
        return DollarSign;
      case "price_reset":
        return RotateCcw;
      case "floor_ceiling_override":
        return SlidersHorizontal;
      case "season_edit":
        return CalendarDays;
      case "competitor_change":
        return Target;
      case "pms_connect":
        return Cable;
      default:
        return MessageCircle;
    }
  }
  switch (source) {
    case "email_inbound":
    case "cs_ticket":
      return Mail;
    case "email_outbound":
      return Send;
    case "stripe":
      return CreditCard;
    case "note":
      return StickyNote;
    case "feedback":
      return Star;
    case "fireflies":
    case "granola":
      return Phone;
    case "posthog":
      return Activity;
    default:
      return Circle;
  }
}

function EventGlyph({
  source,
  type,
}: {
  source: CommunicationSource;
  type: string;
}) {
  const Icon = getEventIcon(source, type);
  return (
    <div
      className="shrink-0 mt-1 w-4 h-4 text-content-tertiary"
      aria-hidden="true"
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source filter chips
// ---------------------------------------------------------------------------

// Meetings (source=fireflies) live on their own top-level "Meetings" tab —
// excluded here so they don't double-render in the Communications timeline.
// Granola is reserved for future meeting-vendor expansion; same exclusion.
const COMMUNICATIONS_SOURCES_CSV =
  "cs_ticket,email_inbound,email_outbound,slack,stripe,note,feedback";

const SOURCE_FILTERS_COMMUNICATIONS: { label: string; value: string }[] = [
  { label: "All", value: "" },
  { label: "Tickets", value: "tickets" },
  { label: "Slack", value: "slack" },
  { label: "Stripe", value: "stripe" },
  { label: "Notes", value: "note" },
  { label: "Feedback", value: "feedback" },
];

// App Activity tab is sourced from PostHog only; chips slice the PostHog
// stream by event-type rather than by source.
const SOURCE_FILTERS_APP_ACTIVITY: { label: string; value: string }[] = [
  { label: "All", value: "" },
  { label: "Daily summaries", value: "daily" },
  { label: "Key events", value: "key" },
];

const SOURCE_CHIP_TO_ENUMS: Record<string, string> = {
  tickets: "cs_ticket,email_inbound,email_outbound",
  // For app-activity mode we hardcode source=posthog at the section level;
  // these "daily/key" chips are filtered client-side on event.type instead.
};

const EMAIL_LIKE_SOURCES: CommunicationSource[] = [
  "cs_ticket",
  "email_inbound",
  "email_outbound",
];

function isEmailLikeSource(source: CommunicationSource): boolean {
  return EMAIL_LIKE_SOURCES.includes(source);
}

function truncateForDisplay(text: string, maxChars = 3000): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return trimmed.slice(0, maxChars) + "\n\n… (truncated)";
}

// ---------------------------------------------------------------------------
// Single event row
// ---------------------------------------------------------------------------

function EventRow({
  event,
  onReply,
}: {
  event: CommunicationEvent;
  onReply?: (ctx: ComposeContext) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const hasBody = Boolean(
    event.searchableText?.trim() ||
      event.payloadRef ||
      (event.metadata && Object.keys(event.metadata).length > 0),
  );

  const threadId = (event.metadata as any)?.threadId as string | undefined;
  const canReply =
    Boolean(onReply) &&
    REPLYABLE_SOURCES.includes(event.source) &&
    Boolean(threadId);
  const parentSubject =
    ((event.metadata as any)?.subject as string | undefined) ??
    event.summary?.replace(/^CS ticket:\s*/i, "") ??
    "";
  const parentSnippet = event.searchableText?.trim()
    ? event.searchableText.trim().slice(0, 240)
    : undefined;

  const handleReplyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onReply || !threadId) return;
    onReply({ mode: "reply", threadId, parentSubject, parentSnippet });
  };

  return (
    <li className="rounded-lg border border-border-secondary bg-surface-primary overflow-hidden">
      <div
        className={`group flex items-start gap-3 px-3.5 py-2.5 transition-colors ${hasBody ? "cursor-pointer hover:bg-gray-50/70" : ""}`}
        onClick={() => hasBody && setExpanded((e) => !e)}
      >
        <EventGlyph source={event.source} type={event.type} />

        <div className="flex-1 min-w-0">
          <p className="text-sm text-content-primary break-words leading-snug">
            {event.summary}
            <span className="ml-2 text-[11px] text-content-tertiary font-normal">
              {event.type.replace(/_/g, " ")}
            </span>
          </p>
        </div>

        {canReply && (
          <button
            type="button"
            onClick={handleReplyClick}
            className="shrink-0 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Reply
          </button>
        )}

        <span
          className="relative shrink-0 text-xs text-content-tertiary cursor-default tabular-nums"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {relativeTime(event.occurredAt)}
          {hovered && (
            <span className="absolute bottom-full right-0 mb-1.5 z-50 px-2.5 py-1.5 rounded bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg pointer-events-none">
              {absoluteTime(event.occurredAt)}
            </span>
          )}
        </span>
      </div>

      {expanded && hasBody && (
        <div
          className="px-3.5 pb-3 pt-1 border-t border-border-secondary bg-gray-50/30"
          onClick={(e) => e.stopPropagation()}
        >
          {event.searchableText?.trim() &&
            (event.source === "slack" || event.source === "fireflies" ? (
              <div className="max-h-[420px] overflow-auto pr-1">
                <div className="prose prose-sm max-w-none prose-headings:text-content-primary prose-p:text-content-primary prose-li:text-content-primary prose-table:text-[11px] prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-pre:bg-surface-secondary text-content-primary">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {truncateForDisplay(event.searchableText)}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-content-primary whitespace-pre-wrap break-words leading-relaxed max-h-[420px] overflow-auto">
                {truncateForDisplay(event.searchableText)}
              </div>
            ))}

          {event.payloadRef && (
            <div className="mt-2 text-[11px] text-content-tertiary">
              Payload ref: <span className="font-mono">{event.payloadRef}</span>
            </div>
          )}

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <details className="group mt-2">
              <summary className="cursor-pointer text-[11px] text-content-tertiary hover:text-content-secondary select-none list-none">
                <span className="group-open:hidden">Show metadata</span>
                <span className="hidden group-open:inline">Hide metadata</span>
              </summary>
              <pre className="mt-1 bg-surface-secondary p-2 rounded text-[11px] overflow-x-auto">
                {JSON.stringify(event.metadata, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Thread grouping
// ---------------------------------------------------------------------------

interface ThreadGroup {
  threadId: string;
  events: CommunicationEvent[];
  latestEvent: CommunicationEvent;
}

type TimelineItem =
  | { kind: "single"; event: CommunicationEvent; sortKey: string }
  | { kind: "thread"; thread: ThreadGroup; sortKey: string };

function groupIntoThreads(events: CommunicationEvent[]): TimelineItem[] {
  const threadBuckets = new Map<string, CommunicationEvent[]>();
  const singles: CommunicationEvent[] = [];

  for (const event of events) {
    const threadId = (event.metadata as any)?.threadId as string | undefined;
    if (threadId && isEmailLikeSource(event.source)) {
      if (!threadBuckets.has(threadId)) threadBuckets.set(threadId, []);
      threadBuckets.get(threadId)!.push(event);
    } else {
      singles.push(event);
    }
  }

  const items: TimelineItem[] = [];

  threadBuckets.forEach((bucket, threadId) => {
    if (bucket.length === 1) {
      singles.push(bucket[0]);
      return;
    }
    const chronological = [...bucket].sort(
      (a, b) =>
        new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
    );
    const latestEvent = chronological[chronological.length - 1];
    items.push({
      kind: "thread",
      thread: { threadId, events: chronological, latestEvent },
      sortKey: latestEvent.occurredAt,
    });
  });

  for (const event of singles) {
    items.push({ kind: "single", event, sortKey: event.occurredAt });
  }

  items.sort(
    (a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime(),
  );

  return items;
}

// ---------------------------------------------------------------------------
// Date bucketing
// ---------------------------------------------------------------------------

function bucketByDate(
  items: TimelineItem[],
): Array<{ label: string; items: TimelineItem[] }> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const buckets: Record<string, TimelineItem[]> = {};
  const order: string[] = [];

  for (const item of items) {
    const itemDate = new Date(item.sortKey);
    let label: string;
    if (itemDate >= today) label = "Today";
    else if (itemDate >= yesterday) label = "Yesterday";
    else if (itemDate >= sevenDaysAgo) label = "This week";
    else if (itemDate >= thirtyDaysAgo) label = "This month";
    else {
      label = itemDate.toLocaleDateString("en-US", {
        timeZone: "America/New_York",
        month: "long",
        year: "numeric",
      });
    }

    if (!buckets[label]) {
      buckets[label] = [];
      order.push(label);
    }
    buckets[label].push(item);
  }

  return order.map((label) => ({ label, items: buckets[label] }));
}

function DateDivider({ label }: { label: string }) {
  return (
    <li className="px-3 pt-5 pb-1.5 first:pt-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary">
        {label}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Thread card — multi-message email threads
// ---------------------------------------------------------------------------

function ThreadCard({
  thread,
  customerId,
  onReply,
}: {
  thread: ThreadGroup;
  customerId: string;
  onReply?: (ctx: ComposeContext) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const qc = useQueryClient();
  const latest = thread.latestEvent;
  const count = thread.events.length;

  const parentSubject =
    ((latest.metadata as any)?.subject as string | undefined) ??
    latest.summary?.replace(/^CS ticket:\s*/i, "") ??
    "";

  const handleReplyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onReply) return;
    onReply({
      mode: "reply",
      threadId: thread.threadId,
      parentSubject,
      parentSnippet: latest.searchableText?.trim().slice(0, 240),
    });
  };

  const close = useMutation({
    mutationFn: () => closeThread(customerId, thread.threadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "customers", customerId] });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
    },
  });

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (close.isPending) return;
    if (
      window.confirm(
        "Mark this thread as closed? Any open tickets on the thread will be marked stale; history stays intact.",
      )
    ) {
      close.mutate();
    }
  };

  return (
    <li className="rounded-lg border border-border-secondary bg-surface-primary overflow-hidden">
      <div
        className="group flex items-start gap-3 px-3.5 py-2.5 cursor-pointer hover:bg-gray-50/70 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <EventGlyph source={latest.source} type={latest.type} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-content-primary break-words leading-snug">
            {latest.summary}
            <span className="ml-2 text-[11px] text-content-tertiary font-normal">
              {count} messages
            </span>
          </p>
        </div>
        {onReply && (
          <button
            type="button"
            onClick={handleReplyClick}
            className="shrink-0 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Reply
          </button>
        )}
        <button
          type="button"
          onClick={handleCloseClick}
          disabled={close.isPending}
          className="shrink-0 text-xs font-medium text-content-tertiary hover:text-content-primary disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Mark this thread as closed — sets any open tickets on it to stale"
        >
          {close.isPending ? "Closing…" : "Close"}
        </button>
        <span
          className="relative shrink-0 text-xs text-content-tertiary cursor-default tabular-nums"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {relativeTime(latest.occurredAt)}
          {hovered && (
            <span className="absolute bottom-full right-0 mb-1.5 z-50 px-2.5 py-1.5 rounded bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg pointer-events-none">
              {absoluteTime(latest.occurredAt)}
            </span>
          )}
        </span>
      </div>
      {expanded && (
        <div
          className="px-3.5 pb-3 pt-3 border-t border-border-secondary bg-gray-50/30 space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          {thread.events.map((ev) => (
            <div key={ev.id}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-[11px] font-medium text-content-secondary">
                  {ev.source === "email_outbound" ? "Sent" : "Received"}
                </span>
                <span className="text-[11px] text-content-tertiary">
                  {shortTime(ev.occurredAt)}
                </span>
              </div>
              <p className="text-[12px] font-medium text-content-primary mb-1">
                {ev.summary}
              </p>
              {ev.searchableText?.trim() && (
                <div className="text-[12px] text-content-primary whitespace-pre-wrap break-words leading-relaxed max-h-[320px] overflow-auto">
                  {truncateForDisplay(ev.searchableText, 2000)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

type TimelineMode = "communications" | "app-activity";

interface Props {
  customerId: string;
  onReply?: (ctx: ComposeContext) => void;
  /** Toggle between the Communications timeline and the PostHog-backed
   *  App Activity timeline. Defaults to "communications" for back-compat. */
  mode?: TimelineMode;
}

export function TimelineSection({
  customerId,
  onReply,
  mode = "communications",
}: Props) {
  const [sourceFilter, setSourceFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [allEvents, setAllEvents] = useState<CommunicationEvent[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const lastProcessedKey = useRef<string | null>(null);

  const isAppActivity = mode === "app-activity";
  const SOURCE_FILTERS = isAppActivity
    ? SOURCE_FILTERS_APP_ACTIVITY
    : SOURCE_FILTERS_COMMUNICATIONS;

  // Source param sent to the timeline API.
  // - app-activity: always posthog (chips slice client-side by event.type)
  // - communications: explicit non-posthog allow-list when "All" is active,
  //   so PostHog rows never bleed onto this tab.
  const apiSourceParam = isAppActivity
    ? "posthog"
    : sourceFilter
    ? SOURCE_CHIP_TO_ENUMS[sourceFilter] ?? sourceFilter
    : COMMUNICATIONS_SOURCES_CSV;

  const { data, isFetching, isError } = useCustomerTimeline(customerId, {
    source: apiSourceParam,
    offset,
  });

  const handleFilterChange = (val: string) => {
    setSourceFilter(val);
    setOffset(0);
    setAllEvents([]);
    setHasMore(true);
    lastProcessedKey.current = null;
  };

  useEffect(() => {
    if (!data) return;
    const key = `${sourceFilter}-${offset}`;
    if (lastProcessedKey.current === key) return;
    lastProcessedKey.current = key;

    const incoming = data.events ?? [];
    setAllEvents((prev) => {
      if (offset === 0) return incoming;
      const seen = new Set(prev.map((e) => e.id));
      return [...prev, ...incoming.filter((e) => !seen.has(e.id))];
    });
    setHasMore(incoming.length >= TIMELINE_PAGE_SIZE);
  }, [data, offset, sourceFilter]);

  const handleLoadMore = () => {
    setOffset((prev) => prev + TIMELINE_PAGE_SIZE);
  };

  // App-activity chips slice the same fetched stream by event.type — the
  // backend only knows source=posthog, so this filter is client-side.
  const visibleEvents = isAppActivity
    ? allEvents.filter((e) => {
        if (sourceFilter === "daily") return e.type === "daily_activity_summary";
        if (sourceFilter === "key") return e.type !== "daily_activity_summary";
        return true;
      })
    : allEvents;

  const buckets =
    visibleEvents.length > 0
      ? bucketByDate(groupIntoThreads(visibleEvents))
      : [];

  return (
    <section className="bg-surface-primary rounded-lg border border-border-secondary shadow-sm">
      <div className="px-6 pt-5 pb-2">
        <h2 className="text-sm font-semibold text-content-primary">
          {isAppActivity ? "App Activity" : "Communications"}
        </h2>
        {isAppActivity && (
          <p className="mt-0.5 text-[11px] text-content-tertiary">
            In-app behavior captured via PostHog · daily rollup + key actions
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 px-6 pb-3 overflow-x-auto">
        {SOURCE_FILTERS.map((f) => {
          const isActive = sourceFilter === f.value;
          return (
            <button
              key={f.value || "all"}
              onClick={() => handleFilterChange(f.value)}
              className={`shrink-0 px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors ${
                isActive
                  ? "bg-gray-900 text-white"
                  : "text-content-secondary hover:bg-gray-100"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {isError && (
        <div className="px-6 py-4 text-sm text-red-700">
          Failed to load events.
        </div>
      )}

      {buckets.length > 0 && (
        <ul className="px-3 pb-3 space-y-1.5">
          {buckets.map(({ label, items }) => (
            <Fragment key={label}>
              <DateDivider label={label} />
              {items.map((item) =>
                item.kind === "thread" ? (
                  <ThreadCard
                    key={`thread-${item.thread.threadId}`}
                    thread={item.thread}
                    customerId={customerId}
                    onReply={onReply}
                  />
                ) : (
                  <EventRow
                    key={item.event.id}
                    event={item.event}
                    onReply={onReply}
                  />
                ),
              )}
            </Fragment>
          ))}
        </ul>
      )}

      {isFetching && (
        <ul className="px-3 pb-3 space-y-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={`sk-${i}`}
              className="flex items-start gap-3 px-3 py-2.5 animate-pulse"
            >
              <div className="w-4 h-4 rounded-sm bg-surface-disabled mt-1" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-3/4 bg-surface-disabled rounded" />
              </div>
              <div className="h-3 w-12 bg-surface-disabled rounded" />
            </li>
          ))}
        </ul>
      )}

      {!isFetching && allEvents.length === 0 && !isError && (
        <div className="flex flex-col items-center justify-center py-12">
          <span className="text-content-tertiary text-sm">
            No recorded communications yet
          </span>
        </div>
      )}

      {!isFetching && hasMore && allEvents.length > 0 && (
        <div className="px-6 py-3 border-t border-border-secondary">
          <button
            onClick={handleLoadMore}
            className="text-xs font-medium text-content-secondary hover:text-content-primary transition-colors"
          >
            Load more
          </button>
        </div>
      )}
    </section>
  );
}
